/* tslint:disable */
/* eslint-disable */
// ReSharper disable InconsistentNaming

import { CustomerFreightTerms, IncoTerms, PartyRole, ShipmentRole, StopType } from '@/common/constants/booking-types';
import { DocumentStatus, DocumentType } from '@/common/constants/document-types';

export interface BookingDocumentsQueryResult {
  documents?: Document[];
}

export interface DownloadBookingDocumentsQueryResult {
  content?: string;
  mimeType?: string;
  fileName?: string;
}

export interface ReadBookingResult {
  booking?: VisibilityBooking;
}

export interface Address {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  county?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  addressNumber?: number;
  residential?: boolean;
  name?: string;
  locationCode?: string;
}

export interface BillTo {
  customer?: Customer;
  referenceNumbers?: ReferenceNumber[];
  onlineContact?: Contact;
  ediFreightTermsRDN?: number;
}

export interface BookingService {
  pickupFrom?: LocationInfo;
  deliverTo?: LocationInfo;
  referenceNumbers?: ReferenceNumber[];
  serviceOffering?: ServiceOffering;
  carrierCode?: string;
  carrierPartyNumber?: number;
  serviceParties?: ServiceParty[];
  itemContainers?: ItemContainer[];
  shippingContainers?: ShippingContainer[];
}

export interface Party {
  partyName?: string;
  partyNumber?: number;
  partyCode?: string;
}

export interface Carrier extends Party {
  referenceNumbers?: ReferenceNumber[];
  name?: string;
  scac?: string;
  address?: Address;
}

export interface CarrierSummary {
  bookingNumber?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  destinationPortArrivedInDays?: number;
  destinationPortArrivalDate?: string;
  destinationPortEstimatedArrivalDate?: string;
  destinationPortEstimatedArrivalInDays?: number;
  destinationPortLocation?: Location;
  destinationAirportCode?: string;
  originPortDepartureDate?: string;
  originPortDepartedInDays?: number;
  originPortEstimatedDepartureDate?: string;
  originPortEstimatedDepartureInDays?: number;
  originPortLocation?: Location;
  originAirportCode?: string;
  departureFeederVesselName?: string;
  departureFeederVesselVoyageNumber?: string;
  arrivalFeederVesselName?: string;
  arrivalFeederVesselVoyageNumber?: string;
}

export interface CommoditySummary {
  commodity?: Commodity;
  packagingCode?: string;
  packagingTypeRDN?: number;
  unitQuantity?: number;
  masterPackQuantity?: number;
  totalWeight?: DecimalValue;
  unitVolume?: DecimalValue;
}

export interface CargoSummary {
  descriptionOfGoods?: string;
  marks?: string;
  quantity?: Quantity;
  totalWeight?: Weight;
  totalVolume?: Volume;
  pallets?: number;
  isHazMat?: boolean;
  isTemperatureControlled?: boolean;
  dimensions?: Dimension[];
}

export interface Commodity {
  id?: number;
  commodityName?: string;
  productName?: string;
}

export interface Contact {
  name?: string;
  emailAddress?: string;
  phoneNumber?: string;
  dialingCode?: string;
}

export interface ContainerDetails {
  containerType?: string;
  containerQuantity?: number;
}

export interface Customer {
  sourcePartyId?: number;
  owningBranch?: Party;
  salesRep?: Employee;
  secondSalesRep?: Employee;
  address?: Address;
  name?: string;
  partyCode?: string;
  partyNumber?: number;
  defaultCurrencyRDN?: number;
  defaultCurrencyRefData?: MdmReferenceData;
  primaryBillToReferenceNumTypeRDN?: number;
  primaryCustomerReferenceNumTypeRDN?: number;
  isTmc?: boolean;
  defaultFreightTermRDN?: number;
}

export interface CustomerInfo {
  customer?: Customer;
  referenceNumbers?: ReferenceNumber[];
  onlineContact?: Contact;
  customerFreightTerms?: CustomerFreightTerms;
}

export interface DecimalValue {
  unitRDN?: number;
  value?: number;
}

export interface Employee extends Party {
  branch?: Party;
  firstName?: string;
  lastName?: string;
  title?: string;
}

export interface ItemContainer {
  containerDetails?: ContainerDetails;

  [key: string]: any;
}

export interface Location extends Party {
  physicalAddress?: Address;
  generalAddress?: Address;
  relatedLocationId?: number;
  lastUsedDateTime?: string;
  name?: string;
}

export interface LocationInfo {
  location?: Location;
  requestedDate?: string;
  readyByDate?: string;
  deliverByDate?: string;
  actualDepartureDateTime?: string;
}

export interface MdmReferenceData {
  number?: number;
  code?: string;
  value?: string;
  description?: string;
  sortOrder?: number;
}

export interface Package {
  packageId?: number;
  serviceId?: number;
  packageTypeRdn?: number;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  volume?: number;
  codAmount?: number;
  declaredValue?: number;
  declaredValueCurrencyCodeRDN?: number;
  declaredValueCurrencyRefData?: MdmReferenceData;
  shippingNotes?: string;
  trackingNumber?: string;
  onlinePackageId?: number;
}

export enum PackageUom {
  None = 0,
  Bundle = 1,
  Carton = 2,
  Crate = 3,
  Drum = 4,
  Envelope = 5,
  JerryCan = 6,
  LoosePiece = 7,
  Pail = 8,
  Pallet = 9,
  Roll = 10,
  Skid = 11,
  Tube = 12,
  Gaylord = 13,
  Bag = 14,
  Box = 15,
  Case = 16,
  Package = 17,
  M2 = 18,
}

export enum WeightUom {
  Lbs = 1,
  Kgs = 2,
}

export enum VolumeUom {
  Cft = 1,
  Cbm = 2,
}

export enum DimensionUom {
  In = 1,
  Cm = 2,
}

export enum TemperatureUom {
  Fahrenheit = 1,
  Celsius = 2,
}

export interface Quantity {
  value?: number;
  unit?: PackageUom;
}

export interface Weight {
  value?: number;
  unit?: WeightUom;
}

export interface Volume {
  value?: number;
  unit?: VolumeUom;
}

export interface Dimension {
  length?: number;
  width?: number;
  height?: number;
  volume?: number;
  stackable?: boolean;
  quantity?: Quantity;
}

export interface Temperature {
  value?: number;
  unit?: TemperatureUom;
}

export interface HazMatDetail {
  properName?: string;
  hazMatClass?: string;
  subRisk?: string;
  unNumber?: number;
  packingGroup?: string;
  packingDetails?: string;
  chemicalName?: string;
  flashPoint?: number;
  emsNumber?: string;
  emergencyPhone?: string;
  isLimitedQuantities?: boolean;
  isMarinePollutant?: boolean;
  isSevereMarinePollutant?: boolean;
}

export interface NonPurchaseOrderItem {
  commodityItemId?: number;
  descriptionOfGoods?: string;
  commodity?: string;
  marks?: string;
  quantity?: Quantity;
  totalWeight?: Weight;
  totalVolume?: Volume;
  pallets?: number;
  sku?: string;
  poNumber?: string;
  itemId?: string;
  isHazMat?: boolean;
  dimensions?: Dimension[];
  hazMatDetail?: HazMatDetail;
  isTemperatureControlled?: boolean;
  minTemperature?: Temperature;
  maxTemperature?: Temperature;
}

export interface PurchaseOrderItem {
  purchaseOrderItemId?: number;
  outerPackQty?: number;
  innerPackQty?: number;
  description?: string;
  destination?: string;
  htCode?: string;
  itemId?: string;
  packagingCode?: string;
  purchaseOrderNumber?: string;
  sku?: string;
  volume?: DecimalValue;
  weight?: DecimalValue;
  isHazMat?: boolean;
  isTemperatureControlled?: boolean;
}

export interface ReferenceData {
  refDataNumber?: number;
  code?: string;
  description?: string;
}

export interface ReferenceNumber {
  refNumTypeRdn?: number;
  value?: string;
  referenceNumberType?: ReferenceData;
  isPrimary?: boolean;
  entity?: string;
}

export interface RelatedBooking {
  bookingNumber?: number;
  status?: number;
  shipmentNumber?: string;
}

export interface RelatedPurchaseOrder {
  customerPurchaseOrderNumber?: string;
  purchaseOrderId?: number;
  customerCode?: string;
  status?: string;
}

export interface ServiceOffering {
  id?: number;
  description?: string;
  modeId?: number;
  modeDescription?: string;
  serviceTypeId?: number;
  serviceTypeDescription?: string;
  capacity?: string;
}

export interface ServiceParty extends Party {
  partyRole?: PartyRole;
}

export interface ShippingContainer {
  containerId?: number;
  containerTypeRdn?: number;
  containerTypeRefData?: MdmReferenceData;
  containerStatusRdn?: number;
  declaredValue?: number;
  weightNet?: number;
  weightGross?: number;
  vgm?: number;
  sealNum?: string;
  containerNumber?: string;
  weightUnitRdn?: number;
  currencyRdn?: number;
  currencyRefData?: MdmReferenceData;
  onlineContainerId?: number;
}

export interface Status {
  id?: number;
  code?: string;
  description?: string;
  value?: string;
}

export interface VisibilityBooking {
  isHazmat?: boolean;
  isTemperatureControlled?: boolean;
  notifyParty?: Party;
  canAccessShipment?: boolean;
  hasShipmentFetchError?: boolean;
  isLinkedToGfsShipment?: boolean;
  plannedOnShipmentDate?: string;
  billTos?: BillTo[];
  conditionRdn?: number;
  createDateTime?: string;
  customerInfo?: CustomerInfo;
  customerBookingNumber?: number;
  commoditySummary?: CommoditySummary;
  cargoSummary?: CargoSummary;
  referenceNumbers?: ReferenceNumber[];
  relatedPurchaseOrders?: RelatedPurchaseOrder[];
  relatedBookings?: RelatedBooking[];
  purchaseOrderItems?: PurchaseOrderItem[];
  nonPurchaseOrderItems?: NonPurchaseOrderItem[];
  services?: BookingService[];
  shipmentNumber?: string;
  status?: Status;
  trackingNumber?: string;
  updatedDateTime?: string;
  incoTerms?: IncoTerms;
  shipmentRole?: ShipmentRole;
  shipment?: VisibilityShipment;
  extraIds?: { [key: string]: ExtraId[] };
  dataSourceLabels?: { [key: string]: DataSourceLabel[] };
}

export interface MeasurementValue {
  metric: string | undefined;
  standard: string | undefined;
}

export interface ExtraIdValue {
  values: string[] | undefined;
  measurementValues: MeasurementValue | undefined;
}

export interface ExtraId {
  position: number;
  label: string;
  customDisplayLabel?: string;
  dataSource: string;
  extraIdValues: ExtraIdValue;
}

export interface DataSourceLabel {
  dataSource: string;
  label: string;
  tenantId: string;
  customerCode: string;
  customerName: string;
}

export interface Document {
  id?: number;
  name?: string;
  description?: string;
  type?: DocumentType;
  bookingNumber?: number;
  loadNumber?: number;
  status?: DocumentStatus;
  createdDate?: string;
  fileType?: string;
}

export interface Appointment {
  closeDateTime?: string;
  openDateTime?: string;
}

export interface StopActivity {
  appointment?: Appointment;
  arrivedDateTime?: string;
  requestedDateTime?: string;
  departureDateTime?: string;
  eta?: string;
  etd?: string;
  displayEta?: string;
  calculatedEta?: string;
  calculatedEtaSource?: string;
  displayEtaSource?: string;
  itemContainerId?: string;
  itemIds?: string[];
  bookIds?: string[];
  sealNumber?: string;
  stopActivityId?: string;
}

export interface ShipmentStop {
  activities?: StopActivity[];
  location?: Location;
  stopId?: string;
  stopNumber?: number;
  style?: string;
  type?: StopType;
  riskLevel?: string;
}

export interface VisibilityShipment {
  deliverByDate?: string;
  pickUpReadyByDate?: string;
  shipmentId?: string;
  shipperName?: string;
  status?: string;
  stops?: ShipmentStop[];
  riskLevel?: string;
  calculatedEtaSource?: string;
  displayEtaSource?: string;
  displayEta?: string;
  chrTrackingNumber?: string;
  billOfLadingNumber?: string;
  primaryCarrierSummary?: CarrierSummary;
  carrierSummaries?: CarrierSummary[];
}

export interface SearchVisibilityOrder {
  id?: string;
  orderNumber?: number;
  status?: string;
  mode?: string;
  sourceSystem?: string;
}

export interface SearchBookingsQueryResult {
  total?: number;
  orders?: SearchVisibilityOrder[];
}
